import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useIdleTimer } from 'react-idle-timer';

import axios from '../utilities/axiosConfig';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

export const AutoLogoutContext = createContext();

export const AutoLogoutProvider = ({ children }) => {
  const [logoutPopupVisible, setLogoutPopupVisible] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(10);
  const [logoutTriggered, setLogoutTriggered] = useState(false);

  const user = Cookies.get('user');
  const token = Cookies.get('token');
  const isVerified = Cookies.get('verified');

  const handleLogout = useCallback(async () => {
    console.log('User logged out');

    if (!logoutTriggered) {
      setLogoutTriggered(true);
      try {
        const response = await axios.post('/user/logout', {
          withCredentials: true,
          credentials: 'include',
        });

        if (Cookies.get('socialverified')) {
          window.open('http://localhost:9001/auth/logout', '_self');
        }
        if (response.data.success === true) {
          localStorage.removeItem('email');
          localStorage.removeItem('name');
          Cookies.remove('token');
          Cookies.remove('user');
          Cookies.remove('temp_secret');
          Cookies.remove('verified');
          localStorage.removeItem('selectedDrawerNavIndex');

          window.location.href = '/';
          // navigate('/');
        }
      } catch (error) {
        console.log('Error while logging out:', error);
      }
    }
  }, [logoutTriggered]);

  const handleIdle = useCallback(() => {
    // Show logout popup and start countdown
    setLogoutPopupVisible(true);
    setLogoutCountdown(10);
  }, []);

  // Define the idle timer configuration
  const idleTimerConfig =
    token && user && isVerified
      ? {
          timeout: 1000 * 60 * 20, // 20 minutes timeout
          promptBeforeIdle: 1000 * 10, // Show prompt 10 seconds before idle
          onIdle: handleIdle,
          events: [
            'mousemove',
            'keydown',
            'wheel',
            'DOMMouseScroll',
            'mouseWheel',
            'mousedown',
            'touchstart',
            'scroll', // Listen for scrolling events
            'resize', // Listen for resize events
            'focus', // Listen for focus events
          ],
          whenIdle: true, // Start idle timer only when tab is out of focus
          whenVisible: false, // Don't start idle timer when tab is visible
        }
      : null;

  // Use the idle timer hook with the conditional configuration
  const { reset } = useIdleTimer(idleTimerConfig || {});

  useEffect(() => {
    // Reset logoutTriggered flag when popup visibility changes
    if (!logoutPopupVisible) {
      setLogoutTriggered(false);
    }
  }, [logoutPopupVisible]);

  useEffect(() => {
    // Start countdown when logoutPopupVisible changes
    if (logoutPopupVisible) {
      const interval = setInterval(() => {
        setLogoutCountdown((prevCountdown) => {
          if (prevCountdown === 0) {
            setLogoutPopupVisible(false);
            clearInterval(interval);
            handleLogout();
          }
          return prevCountdown > 0 ? prevCountdown - 1 : prevCountdown;
        });
      }, 1000);

      return () => clearInterval(interval);
    }

    return undefined;
    // eslint-disable-next-line
  }, [logoutPopupVisible]);

  const resetLogoutTimer = useCallback(() => {
    reset(); // Reset the idle timer
    setLogoutPopupVisible(false); // Hide the logout popup
    setLogoutCountdown(10); // Reset the countdown
    setLogoutTriggered(false);
  }, [reset]);

  const contextValue = useMemo(
    () => ({
      resetLogoutTimer,
    }),
    [resetLogoutTimer],
  );

  return (
    <AutoLogoutContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={logoutPopupVisible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
      >
        <DialogTitle className="DialogTitleBox">
          <Box className="FlexCenter">
            <Box mr={1.5}>
              <WarningAmberOutlinedIcon sx={{ color: 'action.active' }} />
            </Box>
            <Typography
              aria-label="Your session is about to expire"
              tabIndex={0}
              variant="h2"
              component="div"
              className="DialogHeading"
            >
              Your session is about to expire!
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent className="DialogContentBox">
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={12}>
              <Typography variant="body2" component="div">
                You are about to be logged out due to inactivity. Logging out in {logoutCountdown}{' '}
                seconds.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        {/* As of now commented below code as no action are required on the dialog */}
        {/* <DialogActions>
          <Button
            aria-label="cancel"
            onClick={() => {
              setLogoutPopupVisible(false);
              handleLogout();
            }}
            variant="text"
            color="primary"
          >
            Logout
          </Button>
          <Button
            aria-label="Stay Connected"
            onClick={() => window.location.reload()}
            autoFocus
            color="primary"
            variant="contained"
          >
            Stay Connected
          </Button>
        </DialogActions> */}
      </Dialog>
    </AutoLogoutContext.Provider>
  );
  // return (
  //   <AutoLogoutContext.Provider value={contextValue}>
  //     {children}
  //     {/* Your logout popup component */}
  //     <Dialog open={logoutPopupVisible}>
  //       <DialogContent>
  //         <p>
  //           You are about to be logged out due to inactivity. Logging out in {logoutCountdown}{' '}
  //           seconds...
  //         </p>
  //       </DialogContent>
  //     </Dialog>
  //   </AutoLogoutContext.Provider>
  // );
};

AutoLogoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
